import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Pagination } from '@classes/pagination/pagination';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import { PreBillingHistory } from '@classes/pre-billing/preBillingHistory';
import { Site } from '@classes/site';
import { DatatableBaseComponent } from '@components/datatable-base.component';
import { DocumentsService } from '@services/documents.service';
import { PreBillingService } from '@services/preBilling.service';
import { UiService } from '@services/ui.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-pre-billing-history',
  templateUrl: './pre-billing-history.component.html',
  styleUrls: ['./pre-billing-history.component.scss']
})
export class PreBillingHistoryComponent extends DatatableBaseComponent implements OnInit, OnChanges {

  @Input() site: Site;

  private readonly LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER = "PreBillingHistoryDisplayedColumnsMenu";
  private readonly DEFAULT_SORT_VALUE = "-createdOn";

  private _preBillingHistories: PreBillingHistory[] = [];
  dataSource: MatTableDataSource<PreBillingHistory> = new MatTableDataSource<PreBillingHistory>(this._preBillingHistories);
  pagination: Pagination;

  filters = this.fb.group({
    site: [],
    range: new UntypedFormGroup({
      fromStartDate: new UntypedFormControl(null),
      toStartDate: new UntypedFormControl(null),
    }),
  });

  private _sortValue: string;
  private readonly _filterTranslation = [
    { "from": "client", "to": "clientid" },
    { "from": "site", "to": "siteId" },
  ];

  displayedColumns: string[] = [];
  displayedColumnsMenu: any[] = [
    { editable: true, name: 'client', selected: true, visible: false },
    { editable: true, name: 'site', selected: true, visible: false },
    { editable: true, name: 'fileName', selected: true, visible: false },
    { editable: true, name: 'startDate', selected: true, visible: false },
    { editable: true, name: 'endDate', selected: true, visible: false },
    { editable: true, name: 'totalPrice', selected: true, visible: false },
    { editable: true, name: 'createdOn', selected: true, visible: false },
    { editable: false, name: 'actions', selected: true, visible: false },
  ];


  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private _preBillingSrv: PreBillingService,
  ) {
    super(uiSrv);
  }

  ngOnInit(): void {
    this._checkIfColumnsInStorage();
    this.setDisplayedColumns();
    this._resetFitlers();
    this._refreshData();
    this._initSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.site) {
      this._resetFitlers();
      this._refreshData();
    }
  }

  private _refreshData(): void {
    this.isLoading = true;
    this._fetchHistory().subscribe();
  }

  private _fetchHistory(): Observable<PreBillingHistory[]> {
    return this._preBillingSrv.getHistory(this._generatePaginationRequest());
  }

  private _generatePaginationRequest(): PaginationRequest {
    return new PaginationRequest(
      this.filters.value,
      this._sortValue ?? this.DEFAULT_SORT_VALUE,
      this.pagination?.pageNumber,
    );
  }

  private _initSubscriptions(): void {
    this.addSubscription(
      this._preBillingSrv.preBillingHistoriesChanged.subscribe(items => {
        this._preBillingHistories = items;
        this.dataSource.data = this._preBillingHistories;
        this.isLoading = false;
      })
    );

    this.addSubscription(
      this._preBillingSrv.preBillingHistoriesPaginationSubject.subscribe((pagination) => {
        this.pagination = pagination;
      })
    );
  }

  private _checkIfColumnsInStorage(): void {
    if (localStorage.getItem(this.LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER)) {
      this.displayedColumnsMenu = JSON.parse(
        localStorage.getItem(this.LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER)
      );
    }
  }

  public onFilterSubmited(): void {
    this._refreshData();
  }

  public onResetFiltersSubmited(): void {
    this._resetFitlers();
    this._refreshData();
  }

  private _resetFitlers(): void {
    this.filters.reset();
    this.filters.get("site")?.setValue(this.site?.id ?? null);
  }

  public drop(event: CdkDragDrop<string[]>): void {
    // Swap the elements around
    moveItemInArray(
      this.displayedColumnsMenu,
      event.previousIndex,
      event.currentIndex
    );
    this._saveDisplayedColumnsToLocalStorage();
    this.setDisplayedColumns();
  }

  public onSelectMenu(event, name): void {
    this.displayedColumnsMenu.find((f) => f.name === name).selected =
      event.checked;
    this._saveDisplayedColumnsToLocalStorage();
    this.setDisplayedColumns();
  }

  private _saveDisplayedColumnsToLocalStorage(): void {
    localStorage.setItem(
      this.LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER,
      JSON.stringify(this.displayedColumnsMenu)
    );
  }

  private setDisplayedColumns(): void {
    this.displayedColumns = this.displayedColumnsMenu
      .filter((f) => (f || {}).selected)
      .map((m) => m.name);
  }

  public download(history: PreBillingHistory): void {
    this._preBillingSrv.downloadPdf(history.document.id).subscribe();
  }

  public onSortChanged(sortEvent): void {
    let direction = '';

    if (sortEvent.direction === 'asc') {
      direction = '';
    } else if (sortEvent.direction === 'desc') {
      direction = '-';
    }
    if (sortEvent.direction === '') {
      this._sortValue = null;
    } else {
      this._sortValue = ` ${direction}${this._translateForSort(sortEvent.active)}`;
    }

    this._refreshData();
  }

  private _translateForSort(from: string): string {
    return this._filterTranslation.find(item => item.from == from)?.to ?? from;
  }

  public onPageChanged(pageEvent: PageEvent): void {
    this.pagination.pageNumber = pageEvent.pageIndex + 1;
    this._refreshData();
  }

  public deleteFile(history: PreBillingHistory) {
    const confirmation = confirm('Êtes-vous certain de vouloir supprimer ce document ?');

    if (confirmation)
      this._preBillingSrv.deletePdf(history).subscribe();
  }
}
